/**
 * 登录弹窗状态管理
 * 提供全局登录弹窗的显示/隐藏控制，以及统一的登录入口
 */
import { ref, readonly } from 'vue';
import { isWeChat, wechatLoginRedirect } from '@/lib/csd-adapter';

// 弹窗显示状态
const visible = ref(false);

// 是否为注册模式
const isRegisterMode = ref(false);

// 登录成功后的回调函数
const onSuccessCallback = ref<(() => void) | null>(null);

// 登录成功后的重定向路径
const redirectPath = ref<string | null>(null);

/**
 * 打开登录弹窗（内部方法，非微信环境使用）
 */
function openLoginModal(options?: { redirect?: string; onSuccess?: () => void; register?: boolean }) {
  visible.value = true;
  isRegisterMode.value = options?.register || false;
  redirectPath.value = options?.redirect || null;
  onSuccessCallback.value = options?.onSuccess || null;
}

/**
 * 关闭登录弹窗
 */
function closeLoginModal() {
  visible.value = false;
  isRegisterMode.value = false;
  redirectPath.value = null;
  onSuccessCallback.value = null;
}

/**
 * 登录成功处理
 */
function handleLoginSuccess() {
  // 执行回调
  if (onSuccessCallback.value) {
    onSuccessCallback.value();
  }
  // 关闭弹窗
  closeLoginModal();
}

/**
 * 获取重定向路径
 */
function getRedirectPath() {
  return redirectPath.value;
}

/**
 * 统一登录入口
 * 根据环境自动选择登录方式：
 * - 微信 H5 环境 → 跳转微信授权登录
 * - 其他环境 → 弹出登录弹窗
 */
function goToLogin(options?: { redirect?: string; onSuccess?: () => void; register?: boolean }) {
  // 微信 H5 环境：跳转微信授权
  if (isWeChat()) {
    const callbackUrl = window.location.origin + (window.location.pathname || '/');
    wechatLoginRedirect(callbackUrl).catch((err) => {
      console.error('[goToLogin] 微信授权跳转失败，降级为弹窗登录:', err);
      // 降级为弹窗登录
      openLoginModal(options);
    });
    return;
  }

  // 其他环境：弹出登录弹窗
  openLoginModal(options);
}

/**
 * 登录弹窗 composable
 */
export function useLoginModal() {
  return {
    // 状态（只读）
    visible: readonly(visible),
    isRegisterMode: readonly(isRegisterMode),
    // 方法
    goToLogin,
    openLoginModal,
    closeLoginModal,
    handleLoginSuccess,
    getRedirectPath
  };
}
